import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';

import moment from 'moment';
import React, { useEffect } from 'react';
import { z } from 'zod';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { revalidateLogic, useStore } from '@tanstack/react-form';
import Inline from '~/modules/ui-kit/spacings/inline/inline';
import Stack from '~/modules/ui-kit/spacings/stack/stack';
import Spacings from '~/modules/ui-kit/spacings';
import { RecurrentApplicationRule } from '~/components/AvailabilityList';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import messages from './messages';
import { formatWeekday } from '../RecurrenceRules';
import formMessages from '../form-messages';
import { useAppForm } from '../Form';

type AvailabilityFormValues = {
  start: Date;
  end: Date;
  rule: RecurrentApplicationRule;
  hasVehicle?: boolean;
};

type AvailabilityFormProps = {
  onSubmit: (values: AvailabilityFormValues) => void;
  initialValues?: AvailabilityFormValues;
  renderDescription?: (timeFrame: {
    startTime: string;
    endTime: string;
    startingDate: Date;
  }) => React.ReactNode;
  shouldShowVehicleAvailabilityField: boolean;
};

const transformAvailabilityToFormValues = (
  availability: AvailabilityFormValues
) => ({
  weekday: `${
    moment(availability.start).day() === 0
      ? 7
      : moment(availability.start).day()
  }`,
  startingDate: moment(availability.start).format('YYYY-MM-DD'),
  startTime: moment(availability.start).format('HH:mm'),
  endTime: moment(availability.end).format('HH:mm'),
  frequency: availability.rule.type === 1 ? 'weekly' : 'monthly',
  weeklyInterval:
    availability.rule.type === 1 ? `${availability.rule.interval}` : '1',
  monthlyInterval:
    availability.rule.type === 2
      ? `${availability.rule.nthWeekdayOfMonth}`
      : '1',
  hasVehicle: availability.hasVehicle,
});

const schema = (intl: IntlShape) =>
  z
    .object({
      weekday: z.string({
        error: intl.formatMessage(formMessages.required),
      }),
      startingDate: z.string({
        error: intl.formatMessage(formMessages.required),
      }),
      startTime: z.string({
        error: intl.formatMessage(formMessages.required),
      }),
      endTime: z.string({
        error: intl.formatMessage(formMessages.required),
      }),
      frequency: z.union([z.literal('weekly'), z.literal('monthly')]),
      weeklyInterval: z.string(),
      monthlyInterval: z.string({
        error: intl.formatMessage(formMessages.required),
      }),
      hasVehicle: z.boolean(),
    })
    .superRefine((value, ctx) => {
      const start = moment(
        `${value.startingDate}T${value.startTime}`,
        'YYYY-MM-DDTHH:mm'
      )
        .day(Number(value.weekday))
        .startOf('minute');
      const end = moment(
        `${value.startingDate}T${value.endTime}`,
        'YYYY-MM-DDTHH:mm'
      )
        .day(Number(value.weekday))
        .startOf('minute');

      if (end.isSameOrBefore(start)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl.formatMessage(messages.endTimeMustBeBeforeStartTime),
          path: ['endTime'],
        });
      }

      if (Number(value.weeklyInterval) < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl.formatMessage(messages.weeklyIntervalMustBeAtLeastOne),
          path: ['weeklyInterval'],
        });
      }
      if (Number(value.weeklyInterval) > 9) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl.formatMessage(messages.weeklyIntervalMustBeLessThanTen),
          path: ['weeklyInterval'],
        });
      }
    });

const AvailabilityForm = (props: AvailabilityFormProps) => {
  const intl = useIntl();
  const form = useAppForm({
    defaultValues: props.initialValues
      ? transformAvailabilityToFormValues(props.initialValues)
      : {
          weekday: '1',
          startTime: '00:00',
          endTime: '00:00',
          startingDate: moment().add(1, 'week').day(1).format('YYYY-MM-DD'),
          frequency: 'weekly',
          weeklyInterval: '1',
          monthlyInterval: '1',
          hasVehicle: false,
        },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: schema(intl),
    },
    onSubmit({ value }) {
      const start = moment(
        `${value.startingDate}T${value.startTime}`,
        'YYYY-MM-DDTHH:mm'
      )
        .day(Number(value.weekday))
        .startOf('minute');
      const end = moment(
        `${value.startingDate}T${value.endTime}`,
        'YYYY-MM-DDTHH:mm'
      )
        .day(Number(value.weekday))
        .startOf('minute');

      if (end.isSameOrBefore(start)) {
        return;
      }

      let rule: RecurrentApplicationRule;
      if (value.frequency === 'weekly') {
        rule = {
          type: 1,
          interval: Number(value.weeklyInterval),
        };
      } else {
        rule = {
          type: 2,
          nthWeekdayOfMonth: Number(value.monthlyInterval),
          weekday: formatWeekday(
            Number(value.weekday) as Parameters<typeof formatWeekday>[0]
          ),
        };
      }
      props.onSubmit({
        start: start.toDate(),
        end: end.toDate(),
        rule,
        hasVehicle: value.hasVehicle,
      });
    },
  });

  // Use useStore to get reactive form values
  const weekday = useStore(form.store, (state) => state.values.weekday);
  const startingDate = useStore(
    form.store,
    (state) => state.values.startingDate
  );
  const startTime = useStore(form.store, (state) => state.values.startTime);
  const endTime = useStore(form.store, (state) => state.values.endTime);
  const frequency = useStore(form.store, (state) => state.values.frequency);

  // Use useStore to get reactive form errors
  const startTimeErrors = useStore(
    form.store,
    (state) => state.fieldMeta.startTime?.errors
  );
  const endTimeErrors = useStore(
    form.store,
    (state) => state.fieldMeta.endTime?.errors
  );

  // update the starting date according to the selected weekday
  useEffect(() => {
    const weekdayNum = parseInt(weekday || '1', 10);
    const currentStartingDate = startingDate || '';
    const newStartingDate = moment(currentStartingDate)
      .day(weekdayNum === 7 ? 0 : weekdayNum)
      .format('YYYY-MM-DD');

    // Only update if the date actually changed to prevent infinite loop
    if (newStartingDate !== currentStartingDate) {
      form.setFieldValue('startingDate', newStartingDate);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekday]);

  // update the weekday according to the selected starting date
  useEffect(() => {
    const currentStartingDate = startingDate || '';
    const newWeekday = `${
      moment(currentStartingDate).day() === 0
        ? 7
        : moment(currentStartingDate).day()
    }`;

    // Only update if the weekday actually changed to prevent infinite loop
    if (newWeekday !== weekday) {
      form.setFieldValue('weekday', newWeekday);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startingDate]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Spacings.Inset>
        <Spacings.Stack scale="xl" alignItems="flexStart">
          <form.AppField name="weekday">
            {(field) => (
              <field.RadioGroupField
                label={<FormattedMessage {...messages.weekday} />}
                options={Array.from({ length: 7 }).map((_, index) => ({
                  value: `${index + 1}`,
                  label: moment()
                    .day(index + 1)
                    .format('dddd'),
                }))}
              />
            )}
          </form.AppField>
          <Stack scale="m" alignItems="flexStart">
            <FormLabel component="legend">
              <FormattedMessage {...messages.timeFrame} />
            </FormLabel>
            <div>
              <Inline>
                <form.AppField name="startTime">
                  {(field) => (
                    <TimePicker
                      name="startTime"
                      value={moment(
                        `${startingDate || ''}T${field.state.value || ''}`,
                        'YYYY-MM-DDTHH:mm'
                      )}
                      onChange={(momentDate) => {
                        if (!momentDate || !momentDate.isValid()) {
                          return field.handleChange('');
                        }
                        return field.handleChange(
                          momentDate?.format('HH:mm') || ''
                        );
                      }}
                      label={<FormattedMessage {...messages.start} />}
                      sx={{ maxWidth: '150px' }}
                      slotProps={{
                        textField: {
                          error: field.state.meta.errors?.length > 0,
                        },
                      }}
                    />
                  )}
                </form.AppField>
                <form.AppField name="endTime">
                  {(field) => (
                    <TimePicker
                      name="endTime"
                      value={moment(
                        `${startingDate || ''}T${field.state.value || ''}`,
                        'YYYY-MM-DDTHH:mm'
                      )}
                      onChange={(momentDate) => {
                        if (!momentDate || !momentDate.isValid()) {
                          return field.handleChange('');
                        }
                        return field.handleChange(
                          momentDate?.format('HH:mm') || ''
                        );
                      }}
                      label={<FormattedMessage {...messages.end} />}
                      sx={{ maxWidth: '150px' }}
                      slotProps={{
                        textField: {
                          error: field.state.meta.errors?.length > 0,
                        },
                      }}
                    />
                  )}
                </form.AppField>
              </Inline>

              {startTimeErrors && startTimeErrors.length > 0 ? (
                <Typography color="error" variant="body2">
                  {startTimeErrors
                    .flat()
                    .map((error) => error.message)
                    .join(', ')}
                </Typography>
              ) : null}
              {endTimeErrors && endTimeErrors.length > 0 ? (
                <Typography color="error" variant="body2">
                  {endTimeErrors
                    .flat()
                    .map((error) => error.message)
                    .join(', ')}
                </Typography>
              ) : null}
            </div>
            <form.AppField name="startingDate">
              {(field) => (
                <DatePicker
                  name="startingDate"
                  value={moment(field.state.value || '', 'YYYY-MM-DD')}
                  onChange={(momentDate) =>
                    field.handleChange(momentDate?.format('YYYY-MM-DD') || '')
                  }
                  label={<FormattedMessage {...messages.startingDate} />}
                  slotProps={{
                    textField: {
                      error: field.state.meta.errors?.length > 0,
                    },
                  }}
                />
              )}
            </form.AppField>
            {props.shouldShowVehicleAvailabilityField ? (
              <form.AppField name="hasVehicle">
                {(field) => (
                  <field.CheckboxField
                    label={<FormattedMessage {...messages.hasVehicle} />}
                  />
                )}
              </form.AppField>
            ) : null}
          </Stack>
          <form.AppField name="frequency">
            {(field) => (
              <field.RadioGroupField
                label={<FormattedMessage {...messages.frequency} />}
                options={[
                  {
                    value: 'weekly',
                    label: <FormattedMessage {...messages.weekly} />,
                  },
                  {
                    value: 'monthly',
                    label: <FormattedMessage {...messages.monthly} />,
                  },
                ]}
              />
            )}
          </form.AppField>
          {frequency === 'weekly' ? (
            <Stack scale="m">
              <form.AppField name="weeklyInterval">
                {(field) => (
                  <>
                    {/* eslint-disable-next-line jsx-a11y/label-has-for */}
                    <label>
                      <Inline alignItems="center">
                        <FormattedMessage
                          {...messages.weeklyInterval}
                          values={{
                            nthWeek: field.state.value || '1',
                            nth: () => (
                              <Box
                                sx={{
                                  marginX: 2,
                                }}
                              >
                                <field.TextField
                                  label=""
                                  type="number"
                                  style={{ width: '70px' }}
                                  hideError
                                />
                              </Box>
                            ),
                          }}
                        />
                      </Inline>
                    </label>
                    {field.state.meta.errors?.length > 0 ? (
                      <Typography color="error" variant="body2">
                        {field.state.meta.errors
                          .map((error) => error?.message)
                          .join(', ')}
                      </Typography>
                    ) : null}
                  </>
                )}
              </form.AppField>
            </Stack>
          ) : null}
          {frequency === 'monthly' ? (
            <Inline alignItems="center">
              <FormattedMessage
                {...messages.monthlyInterval}
                values={{
                  weekday: moment()
                    .day(Number(weekday || '1'))
                    .format('dddd'),

                  nth: () => (
                    <Box
                      sx={{
                        marginX: 2,
                      }}
                    >
                      <form.AppField name="monthlyInterval">
                        {(field) => (
                          <field.SelectField
                            label={<FormattedMessage {...messages.every} />}
                            options={[
                              {
                                value: '1',
                                label: <FormattedMessage {...messages.first} />,
                              },
                              {
                                value: '2',
                                label: (
                                  <FormattedMessage {...messages.second} />
                                ),
                              },
                              {
                                value: '3',
                                label: <FormattedMessage {...messages.third} />,
                              },
                              {
                                value: '4',
                                label: (
                                  <FormattedMessage {...messages.fourth} />
                                ),
                              },
                              {
                                value: '5',
                                label: <FormattedMessage {...messages.fifth} />,
                              },
                            ]}
                          />
                        )}
                      </form.AppField>
                    </Box>
                  ),
                }}
              />
            </Inline>
          ) : null}
          {props.renderDescription ? (
            <div>
              <Typography variant="body2" color="textSecondary">
                {props.renderDescription({
                  startTime: startTime || '',
                  endTime: endTime || '',
                  startingDate: moment(startingDate || '').toDate(),
                })}
              </Typography>
            </div>
          ) : null}
          <div>
            <form.AppForm>
              <form.SubmitButton>
                <FormattedMessage {...messages.saveAvailability} />
              </form.SubmitButton>
            </form.AppForm>
          </div>
        </Spacings.Stack>
      </Spacings.Inset>
    </form>
  );
};

export default AvailabilityForm;
